import { useState, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { UserPlus, Baby, Crown } from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roleFromUrl = searchParams.get('role');
  const turnstileRef = useRef(null);

  const selectedRole = (roleFromUrl === 'nanny') ? 'nanny' : 'parent';
  const isParent = selectedRole === 'parent';

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: ''
  });
  const [turnstileToken, setTurnstileToken] = useState('');
  const [loading, setLoading] = useState(false);

  const handleTurnstileSuccess = useCallback((token) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileError = useCallback(() => {
    setTurnstileToken('');
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken('');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      return;
    }
    
    setLoading(true);
    
    const result = await register(
      formData.email,
      formData.password,
      `${formData.first_name} ${formData.last_name}`.trim(),
      formData.phone,
      turnstileToken,
      selectedRole,
      formData.first_name,
      formData.last_name
    );
    
    if (result.success) {
      navigate('/accueil');
    } else {
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
      setTurnstileToken('');
    }
    
    setLoading(false);
  };

  const passwordMismatch = formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword;
  const captchaRequired = TURNSTILE_SITE_KEY && !turnstileToken;

  return (
    <div 
      className="min-h-screen flex items-center justify-center py-8"
      style={{ background: 'linear-gradient(135deg, var(--bg-tertiary) 0%, var(--bg-secondary) 100%)' }}
      data-testid="register-page"
    >
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="text-center">
          <div
            className="mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center"
            style={{ backgroundColor: isParent ? 'var(--secondary-400)' : 'var(--primary-500)' }}
          >
            {isParent ? <Baby className="h-8 w-8 text-white" /> : <Crown className="h-8 w-8 text-white" />}
          </div>
          <CardTitle className="text-2xl" style={{ fontFamily: 'var(--font-secondary)' }}>
            {isParent ? 'Inscription Parent' : 'Inscription Nounou'}
          </CardTitle>
          <CardDescription>
            {isParent
              ? 'Créez votre compte Nannyou en tant que parent employeur'
              : 'Créez votre compte Nannyou en tant qu\'assistante maternelle'}
          </CardDescription>
          <Badge
            className="mt-2 mx-auto text-[11px] border-0"
            style={{ backgroundColor: isParent ? 'var(--secondary-100)' : 'var(--primary-100)', color: isParent ? 'var(--secondary-600)' : 'var(--primary-600)' }}
          >
            {isParent ? 'Compte Parent' : 'Compte Nounou'}
          </Badge>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="first_name">Prénom</Label>
                <Input
                  id="first_name"
                  type="text"
                  placeholder={isParent ? 'Marie' : 'Sophie'}
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                  data-testid="firstname-input"
                />
              </div>
              <div>
                <Label htmlFor="last_name">Nom</Label>
                <Input
                  id="last_name"
                  type="text"
                  placeholder={isParent ? 'Dupont' : 'Martin'}
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                  data-testid="lastname-input"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="votre@email.fr"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="phone">Téléphone (optionnel)</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="06 12 34 56 78"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                data-testid="phone-input"
              />
            </div>
            
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={8}
                data-testid="password-input"
              />
              <p className="text-xs text-[var(--text-tertiary)] mt-1">
                Minimum 8 caractères, 1 majuscule, 1 chiffre
              </p>
            </div>
            
            <div>
              <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                data-testid="confirm-password-input"
              />
              {passwordMismatch && (
                <p className="text-xs text-[var(--error)] mt-1">
                  Les mots de passe ne correspondent pas
                </p>
              )}
            </div>

            {/* Cloudflare Turnstile CAPTCHA */}
            {TURNSTILE_SITE_KEY && (
              <div className="flex justify-center" data-testid="turnstile-widget">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={handleTurnstileSuccess}
                  onError={handleTurnstileError}
                  onExpire={handleTurnstileExpire}
                  options={{
                    theme: 'light',
                    size: 'normal',
                    language: 'fr',
                  }}
                />
              </div>
            )}
            
            <Button 
              type="submit" 
              className="w-full h-11 text-base font-semibold text-white" 
              disabled={loading || passwordMismatch || captchaRequired}
              style={{ backgroundColor: isParent ? 'var(--secondary-400)' : 'var(--primary-500)' }}
              data-testid="register-button"
            >
              {loading ? 'Création…' : 'Créer mon compte'}
            </Button>
          </form>
          
          {/* Switch role */}
          <div className="mt-5 text-center">
            <Link
              to={`/register?role=${isParent ? 'nanny' : 'parent'}`}
              className="text-xs underline hover:no-underline"
              style={{ color: 'var(--text-muted)' }}
              data-testid="switch-role-link"
            >
              {isParent ? 'Je suis nounou →' : '← Je suis parent'}
            </Link>
          </div>

          <div className="mt-3 text-center text-sm text-[var(--text-secondary)]">
            Déjà un compte ?{' '}
            <Link to="/login" className="text-[var(--primary-400)] hover:underline" data-testid="login-link">
              Se connecter
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
